import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";

const compressImage = (file, maxPx = 800, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// Extract a cropped region from an image data URL
const getCroppedImg = (imageSrc, pixelCrop, outputSize = 800, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        img,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        outputSize, outputSize
      );
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = imageSrc;
  });

const SERVICE_PRESETS = [
  "Full Grooming",
  "Teddy Bear Cut",
  "Puppy Cut",
  "Lion Cut",
  "Summer Cut",
  "De-shedding & Trim",
  "Sanitary Trim & Bath",
  "Breed-Specific Styling",
  "Cat Grooming & Bath",
  "Cat Lion Cut",
  "Custom Styling",
];

const AdminShowcase = ({ user }) => {
  const [showcases, setShowcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTabPreview, setActiveTabPreview] = useState({}); // { [id]: 'before' | 'after' }

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);

  // Crop modal state
  const [cropModal, setCropModal] = useState(null); // { imageSrc, field: 'beforeImage'|'afterImage' }
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const initialForm = {
    petName: "",
    breed: "",
    service: "Full Grooming",
    petType: "dog",
    beforeImage: "",
    afterImage: "",
    description: "",
    order: 0,
    isActive: true,
  };
  const [formData, setFormData] = useState(initialForm);

  const fetchShowcases = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/showcases?all=true");
      if (res.data.success) {
        setShowcases(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching showcases:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShowcases();
  }, [fetchShowcases]);

  const showNotification = (msg, type = "success") => {
    setStatusMsg({ text: msg, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({
      ...initialForm,
      order: showcases.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      petName: item.petName || "",
      breed: item.breed || "",
      service: item.service || "Full Grooming",
      petType: item.petType || "dog",
      beforeImage: item.beforeImage || "",
      afterImage: item.afterImage || "",
      description: item.description || "",
      order: item.order || 0,
      isActive: item.isActive !== undefined ? item.isActive : true,
    });
    setModalOpen(true);
  };

  const handleImageUpload = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModal({ imageSrc: ev.target.result, field });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropConfirm = async () => {
    if (!cropModal || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(cropModal.imageSrc, croppedAreaPixels);
      setFormData((prev) => ({ ...prev, [cropModal.field]: cropped }));
      setCropModal(null);
    } catch (err) {
      console.error("Crop failed:", err);
      showNotification("Failed to crop image. Please try again.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.petName.trim() || !formData.breed.trim()) {
      showNotification("Please fill in Pet Name and Breed", "error");
      return;
    }
    if (!formData.beforeImage) {
      showNotification("Please upload a BEFORE photo", "error");
      return;
    }
    if (!formData.afterImage) {
      showNotification("Please upload an AFTER photo", "error");
      return;
    }

    try {
      setSubmitting(true);
      const headers = { Authorization: `Bearer ${user.token}` };

      if (editingId) {
        const res = await axios.put(`/api/showcases/${editingId}`, formData, { headers });
        if (res.data.success) {
          showNotification(`Updated transformation for "${formData.petName}"!`);
          fetchShowcases();
          setModalOpen(false);
        }
      } else {
        const res = await axios.post("/api/showcases", formData, { headers });
        if (res.data.success) {
          showNotification(`Added new Before & After transformation for "${formData.petName}"!`);
          fetchShowcases();
          setModalOpen(false);
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      showNotification(err.response?.data?.message || "Failed to save transformation", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      await axios.delete(`/api/showcases/${id}`, { headers });
      showNotification("Transformation deleted successfully");
      setDeleteConfirm(null);
      fetchShowcases();
    } catch (err) {
      console.error("Delete error:", err);
      showNotification("Failed to delete transformation", "error");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      await axios.put(`/api/showcases/${item._id}`, { isActive: !item.isActive }, { headers });
      setShowcases((prev) =>
        prev.map((s) => (s._id === item._id ? { ...s, isActive: !s.isActive } : s))
      );
      showNotification(
        `Transformation for "${item.petName}" is now ${!item.isActive ? "Visible on Landing Page" : "Hidden"}`
      );
    } catch (err) {
      console.error("Toggle error:", err);
      showNotification("Failed to update visibility", "error");
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm("Are you sure you want to reset Before & After gallery to default photos? Custom additions will be overwritten.")) {
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.post("/api/showcases/seed", {}, { headers });
      if (res.data.success) {
        showNotification("Gallery reset to default Before & After photos!");
        fetchShowcases();
      }
    } catch (err) {
      console.error("Seed error:", err);
      showNotification("Failed to reset showcases", "error");
    }
  };

  const filteredShowcases = showcases.filter((item) => {
    const matchesFilter =
      filter === "all"
        ? true
        : filter === "inactive"
        ? !item.isActive
        : item.petType === filter;

    const matchesSearch =
      item.petName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.breed?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.service?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {statusMsg && (
        <div
          className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl font-bold text-sm flex items-center gap-2 animate-in slide-in-from-top-4 duration-300 ${
            statusMsg.type === "error"
              ? "bg-red-500 text-white"
              : "bg-emerald-600 text-white"
          }`}
        >
          <span>{statusMsg.type === "error" ? "⚠️" : "✨"}</span>
          <span>{statusMsg.text}</span>
        </div>
      )}

      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-[#6992a5]/15 via-purple-50 to-[#46735c]/15 p-6 rounded-3xl border border-[#e1ebf0]">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">✨</span>
            <h2 className="text-xl font-black text-gray-800">
              Landing Page Before &amp; After Photos
            </h2>
          </div>
          <p className="text-xs text-gray-500 font-medium">
            Manage the pet transformation showcase shown on your landing page. Changes reflect immediately!
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            onClick={handleResetDefaults}
            className="px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
            title="Reset gallery to default showcase photos"
          >
            <span>🔄</span> Reset Defaults
          </button>
          <button
            onClick={handleOpenAdd}
            className="px-5 py-2.5 bg-gradient-to-r from-[#6992a5] to-[#46735c] hover:opacity-95 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5"
          >
            <span>➕</span> Add New Transformation
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="flex gap-1.5 bg-gray-100 p-1.5 rounded-2xl w-full sm:w-auto overflow-x-auto">
          {[
            { id: "all", label: "All Items", icon: "🐾", count: showcases.length },
            { id: "dog", label: "Dogs", icon: "🐶", count: showcases.filter((s) => s.petType === "dog").length },
            { id: "cat", label: "Cats", icon: "🐱", count: showcases.filter((s) => s.petType === "cat").length },
            { id: "inactive", label: "Hidden", icon: "👁️‍🗨️", count: showcases.filter((s) => !s.isActive).length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                filter === tab.id
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  filter === tab.id ? "bg-purple-100 text-purple-700" : "bg-gray-200 text-gray-600"
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search by pet, breed, service..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 pl-9 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-purple-400 transition-all shadow-sm"
          />
          <span className="absolute left-3 top-3 text-xs text-gray-400">🔍</span>
        </div>
      </div>

      {/* Showcase Cards Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-xs font-bold text-gray-400">Loading transformation photos...</p>
        </div>
      ) : filteredShowcases.length === 0 ? (
        <div className="bg-white rounded-3xl border-2 border-dashed border-gray-200 p-12 text-center flex flex-col items-center gap-3">
          <span className="text-4xl">📸</span>
          <h3 className="text-base font-black text-gray-700">No Transformations Found</h3>
          <p className="text-xs text-gray-400 max-w-sm">
            {searchQuery
              ? "No items match your search. Try adjusting the query."
              : "No Before & After photos added yet. Click 'Add New Transformation' above to start showcasing your work!"}
          </p>
          <button
            onClick={handleOpenAdd}
            className="mt-2 px-5 py-2.5 bg-[#46735c] text-white font-bold rounded-xl text-xs hover:bg-[#395e4b] transition-all"
          >
            Add First Transformation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredShowcases.map((item) => {
            const previewView = activeTabPreview[item._id] || "after";

            return (
              <div
                key={item._id}
                className={`bg-white rounded-3xl border overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col ${
                  item.isActive ? "border-gray-200" : "border-amber-200 bg-amber-50/20 opacity-80"
                }`}
              >
                {/* Photo Comparison Display */}
                <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden group">
                  <img
                    src={previewView === "after" ? item.afterImage : item.beforeImage}
                    alt={`${item.petName} - ${previewView}`}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105"
                  />

                  {/* Gradient bottom overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

                  {/* Before / After Toggle Buttons */}
                  <div className="absolute top-3 left-3 flex bg-black/60 backdrop-blur-md p-1 rounded-2xl border border-white/20 z-10">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveTabPreview((p) => ({ ...p, [item._id]: "before" }))
                      }
                      className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition-all ${
                        previewView === "before"
                          ? "bg-amber-400 text-gray-900 shadow-sm"
                          : "text-white/80 hover:text-white"
                      }`}
                    >
                      Before
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveTabPreview((p) => ({ ...p, [item._id]: "after" }))
                      }
                      className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition-all ${
                        previewView === "after"
                          ? "bg-emerald-400 text-gray-900 shadow-sm"
                          : "text-white/80 hover:text-white"
                      }`}
                    >
                      After ✨
                    </button>
                  </div>

                  {/* Tag Pill */}
                  <div className="absolute top-3 right-3 z-10">
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold backdrop-blur-md ${
                        item.isActive
                          ? "bg-white/90 text-purple-700 shadow-sm"
                          : "bg-amber-400 text-gray-900"
                      }`}
                    >
                      {item.petType === "cat" ? "🐱 Cat" : "🐶 Dog"}
                    </span>
                  </div>

                  {/* Hover hint */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs z-10 pointer-events-none">
                    <span className="font-black text-sm drop-shadow">{item.petName}</span>
                    <span className="text-[10px] bg-black/50 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider backdrop-blur-sm">
                      Showing: {previewView.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-gray-900 text-base">{item.petName}</h4>
                      <span className="text-xs text-gray-500 font-medium">{item.breed}</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-100 px-2.5 py-0.5 rounded-lg">
                        ✂️ {item.service}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 pt-1">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                    {/* Toggle Active Button */}
                    <button
                      onClick={() => handleToggleActive(item)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                        item.isActive
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      }`}
                      title={item.isActive ? "Currently live on landing page. Click to hide." : "Currently hidden. Click to show."}
                    >
                      <span>{item.isActive ? "● Live" : "○ Hidden"}</span>
                    </button>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleOpenEdit(item)}
                        className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                      >
                        <span>✏️</span> Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(item)}
                        className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-xl transition-colors"
                        title="Delete showcase"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
              <div>
                <h3 className="text-xl font-black text-gray-800">
                  {editingId ? "Edit Before & After Transformation" : "Add Before & After Transformation"}
                </h3>
                <p className="text-xs text-gray-400 font-medium">
                  Upload high-quality before and after photos of your grooming work.
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 font-bold flex items-center justify-center transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pet Info Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Pet Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Coco, Bella, Max"
                    value={formData.petName}
                    onChange={(e) => setFormData({ ...formData, petName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Breed <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Shih Tzu, Toy Poodle, Persian Cat"
                    value={formData.breed}
                    onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Service Performed <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-purple-500 bg-white"
                  >
                    {SERVICE_PRESETS.map((s) => (
                      <option key={s} value={s}>
                        ✂️ {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Pet Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.petType}
                    onChange={(e) => setFormData({ ...formData, petType: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-purple-500 bg-white"
                  >
                    <option value="dog">🐶 Dog</option>
                    <option value="cat">🐱 Cat</option>
                    <option value="other">🐾 Other</option>
                  </select>
                </div>
              </div>

              {/* Photo Uploaders (Side by Side) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Before Photo */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[10px] font-black uppercase">
                        BEFORE
                      </span>
                      Photo <span className="text-red-500">*</span>
                    </label>
                    {formData.beforeImage && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, beforeImage: "" })}
                        className="text-[10px] text-red-500 hover:underline font-bold"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="border-2 border-dashed border-amber-200 bg-amber-50/30 rounded-2xl p-4 text-center hover:border-amber-400 transition-colors">
                    {formData.beforeImage ? (
                      <div className="relative aspect-video rounded-xl overflow-hidden shadow-inner group">
                        <img
                          src={formData.beforeImage}
                          alt="Before Preview"
                          className="w-full h-full object-cover"
                        />
                        <label className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer text-xs font-bold">
                          Change Photo
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, "beforeImage")}
                            className="hidden"
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center gap-2 py-6">
                        <span className="text-3xl">📷</span>
                        <span className="text-xs font-bold text-amber-800">
                          Upload BEFORE Photo
                        </span>
                        <span className="text-[10px] text-gray-400">
                          PNG, JPG, WebP (auto-compressed)
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, "beforeImage")}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* After Photo */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-md text-[10px] font-black uppercase">
                        AFTER
                      </span>
                      Photo <span className="text-red-500">*</span>
                    </label>
                    {formData.afterImage && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, afterImage: "" })}
                        className="text-[10px] text-red-500 hover:underline font-bold"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="border-2 border-dashed border-emerald-200 bg-emerald-50/30 rounded-2xl p-4 text-center hover:border-emerald-400 transition-colors">
                    {formData.afterImage ? (
                      <div className="relative aspect-video rounded-xl overflow-hidden shadow-inner group">
                        <img
                          src={formData.afterImage}
                          alt="After Preview"
                          className="w-full h-full object-cover"
                        />
                        <label className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer text-xs font-bold">
                          Change Photo
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, "afterImage")}
                            className="hidden"
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center gap-2 py-6">
                        <span className="text-3xl">✨</span>
                        <span className="text-xs font-bold text-emerald-800">
                          Upload AFTER Photo
                        </span>
                        <span className="text-[10px] text-gray-400">
                          PNG, JPG, WebP (auto-compressed)
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, "afterImage")}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Short Description / Grooming Notes
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Matted scruffy coat transformed into a bouncy Teddy Bear trim..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Options */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActiveToggle"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="isActiveToggle" className="text-xs font-bold text-gray-700 cursor-pointer">
                    Show this transformation live on the landing page
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-bold">Display Order:</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    className="w-16 px-2.5 py-1 text-center border border-gray-200 rounded-lg text-xs font-bold"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-gradient-to-r from-[#6992a5] to-[#46735c] hover:opacity-95 text-white font-bold rounded-xl text-sm shadow-md transition-all disabled:opacity-50"
                >
                  {submitting ? "Saving..." : editingId ? "Save Changes" : "Publish Transformation"}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-xl text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl relative animate-in zoom-in-95 duration-200 text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto text-2xl">
              🗑️
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-800">
                Delete Transformation?
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Are you sure you want to remove the Before &amp; After photo for <strong>{deleteConfirm.petName}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => handleDelete(deleteConfirm._id)}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm shadow-md transition-all"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl text-sm transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {cropModal && (
        <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          {/* Header */}
          <div className="w-full max-w-sm mb-3 flex items-center justify-between px-1">
            <div>
              <h3 className="text-white font-black text-base">
                ✂️ Crop {cropModal.field === "beforeImage" ? "Before" : "After"} Photo
              </h3>
              <p className="text-gray-400 text-[11px] mt-0.5">Drag to position · Scroll or slide to zoom</p>
            </div>
            <button
              onClick={() => setCropModal(null)}
              className="text-gray-400 hover:text-white text-xl font-bold transition-colors p-1"
            >
              ✕
            </button>
          </div>

          {/* Crop area */}
          <div className="relative w-full max-w-sm" style={{ height: 300 }}>
            <Cropper
              image={cropModal.imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>

          {/* Zoom slider */}
          <div className="w-full max-w-sm mt-4 px-1 flex items-center gap-3">
            <span className="text-gray-400 text-xs">🔍</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full accent-[#46735c] cursor-pointer"
            />
            <span className="text-gray-400 text-xs">{zoom.toFixed(1)}×</span>
          </div>

          {/* Action buttons */}
          <div className="w-full max-w-sm mt-4 flex gap-3">
            <button
              onClick={() => setCropModal(null)}
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleCropConfirm}
              className="flex-1 py-3 bg-[#46735c] hover:bg-[#395e4b] text-white font-bold rounded-xl text-sm shadow-lg transition-all"
            >
              ✓ Use This Crop
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShowcase;
